import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTPageLayout from '../common/RTPageLayout';
import { apiGet } from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import './JobReportList.css';
import tenantStorage from '../../utils/tenantStorage';

const RESULT_ALIASES = {
  whatsapp_sent: 'success',
  whatsapp_failed: 'failed',
};

const SUMMARY_ALIASES = {
  whatsapp_sent_count: 'sent_count',
  whatsapp_failed_count: 'failed_count',
};

const RESULT_ROW_OMIT_KEYS = new Set(['template_name']);

const humanizeKey = (key) => {
  if (!key) return '';
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const formatScalar = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const itemFingerprint = (item) => {
  if (item === null || item === undefined) return 'null';
  if (typeof item === 'object' && !Array.isArray(item)) {
    for (const key of [
      'allocation_id',
      'invoice_id',
      'record_id',
      'id',
      'phone_number',
    ]) {
      if (item[key] !== null && item[key] !== undefined) {
        return `${key}:${item[key]}`;
      }
    }
    return JSON.stringify(item);
  }
  return `v:${item}`;
};

const resultsAreEquivalent = (a, b) => {
  if ((!a || a.length === 0) && (!b || b.length === 0)) return true;
  if (!a || !b || a.length !== b.length) return false;
  const setA = new Set(a.map(itemFingerprint));
  const setB = new Set(b.map(itemFingerprint));
  if (setA.size !== setB.size) return false;
  for (const v of setA) {
    if (!setB.has(v)) return false;
  }
  return true;
};

const visibleResultCategories = (results) => {
  if (!isPlainObject(results)) return [];
  return Object.keys(results).filter((key) => {
    const value = results[key];
    if (!Array.isArray(value) || value.length === 0) return false;
    const primary = RESULT_ALIASES[key];
    if (primary && Array.isArray(results[primary])) {
      if (resultsAreEquivalent(value, results[primary])) return false;
    }
    return true;
  });
};

const visibleSummaryEntries = (summary) => {
  if (!isPlainObject(summary)) return [];
  return Object.entries(summary).filter(([key, value]) => {
    if (value === null || value === undefined) return false;
    const primary = SUMMARY_ALIASES[key];
    if (primary !== undefined && summary[primary] === value) return false;
    return true;
  });
};

const ViewJobDetails = ({ jobIdProp, showMenu = true, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId: jobIdParam } = useParams();
  const jobId = jobIdProp || jobIdParam;

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails(jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const fetchJobDetails = async (id) => {
    setLoading(true);
    try {
      const token = tenantStorage.getItem('token');
      const response = await apiGet(`/jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const apiResponse = response.data;
      if (apiResponse) {
        setJob(apiResponse);
      } else {
        toastError('Job not found');
        handleBack();
      }
    } catch (err) {
      console.error('Error loading job details:', err);
      toastError(err.response?.data?.detail || 'Failed to fetch job details');
      handleBack();
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/reports/jobs');
    }
  };

  const handleExportCsv = async () => {
    if (!jobId) return;
    try {
      setExporting(true);
      const token = tenantStorage.getItem('token');
      const response = await apiGet(`/jobs/${jobId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const disposition =
        response.headers?.['content-disposition'] ||
        response.headers?.['Content-Disposition'] ||
        '';
      const headerMatch = /filename="?([^";]+)"?/i.exec(disposition);
      const fallbackName = `${(job?.job_name || `job_${jobId}`).trim()} - export.csv`;
      link.download = headerMatch?.[1] || fallbackName;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastSuccess('Results exported');
    } catch (err) {
      console.error('Error exporting job results:', err);
      toastError(err.response?.data?.detail || 'Failed to export job results');
    } finally {
      setExporting(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const durationMs = endDate - startDate;
    const durationMinutes = Math.floor(durationMs / 60000);
    const durationSeconds = Math.floor((durationMs % 60000) / 1000);
    return `${durationMinutes}m ${durationSeconds}s`;
  };

  const formatJobType = (jobType) => {
    if (!jobType) return '';
    return jobType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderResultItem = (item, index) => {
    if (item === null || item === undefined) return null;
    if (typeof item !== 'object') {
      return (
        <span key={index} className="job-result-chip">
          {String(item)}
        </span>
      );
    }

    const entries = Object.entries(item).filter(
      ([k, v]) =>
        !RESULT_ROW_OMIT_KEYS.has(k) &&
        v !== null &&
        v !== undefined &&
        v !== '',
    );
    return (
      <div key={index} className="job-result-object">
        {entries.map(([k, v]) => (
          <div key={k} className="job-result-object-row">
            <span className="job-result-object-key">{humanizeKey(k)}:</span>{' '}
            <span className="job-result-object-value">
              {Array.isArray(v) ? v.join(', ') : formatScalar(v)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderResultList = (items) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    const allScalar = items.every(
      (item) => item === null || typeof item !== 'object',
    );
    if (allScalar) {
      return (
        <div className="job-invoice-ids">{items.map(String).join(', ')}</div>
      );
    }
    return (
      <div className="job-result-objects">{items.map(renderResultItem)}</div>
    );
  };

  const resultSectionTone = (key) => {
    const k = String(key || '').toLowerCase();
    if (k.includes('fail') || k.includes('error')) return 'text-danger';
    if (k.includes('success') || k.includes('sent')) return 'text-success';
    if (k.includes('warning')) return 'text-warning';
    return '';
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'job-status-badge';

    if (status === 'completed') {
      return `${baseClass} job-status-completed`;
    } else if (status === 'failed') {
      return `${baseClass} job-status-failed`;
    } else if (status === 'processing') {
      return `${baseClass} job-status-processing`;
    } else {
      return `${baseClass} job-status-pending`;
    }
  };

  const parsedDetails = useMemo(() => {
    if (!job?.details) return {};
    try {
      return typeof job.details === 'string'
        ? JSON.parse(job.details)
        : job.details || {};
    } catch (e) {
      console.error('Failed to parse job details:', e);
      return {};
    }
  }, [job]);

  const resultCategories = useMemo(
    () => visibleResultCategories(parsedDetails.results),
    [parsedDetails],
  );

  const summaryEntries = useMemo(
    () => visibleSummaryEntries(parsedDetails.summary),
    [parsedDetails],
  );

  if (loading) {
    const content = (
      <div className="job-spinner-container">
        <RTSpinner message="Loading job details..." />
      </div>
    );

    if (!showMenu) {
      return content;
    }

    return (
      <RTPageLayout activePath={location.pathname}>{content}</RTPageLayout>
    );
  }

  if (!job) {
    return null;
  }

  const hasDetails =
    parsedDetails && Object.keys(parsedDetails).length > 0;

  const content = (
    <>
      <div className="rt-section">
        <h5 className="mb-3" style={{ color: '#153C7F' }}>
          Job Summary
        </h5>
        <div className="row">
          <div className="col-md-6">
            <div className="job-detail-item">
              <strong>Job ID:</strong> {job.job_id}
            </div>
            <div className="job-detail-item">
              <strong>Job Type:</strong> {formatJobType(job.job_type)}
            </div>
            <div className="job-detail-item">
              <strong>Status:</strong>{' '}
              <span className={getStatusBadgeClass(job.status)}>
                {job.status}
              </span>
            </div>
            <div className="job-detail-item">
              <strong>Started At:</strong> {formatTimestamp(job.job_start_time)}
            </div>
            <div className="job-detail-item">
              <strong>Ended At:</strong> {formatTimestamp(job.job_end_time)}
            </div>
          </div>
          <div className="col-md-6">
            <div className="job-detail-item">
              <strong>Total Records:</strong> {job.total_records}
            </div>
            <div className="job-detail-item">
              <strong>Processed Records:</strong> {job.processed_records}{' '}
              {job.status === 'processing' && job.total_records > 0
                ? `(${Math.round((job.processed_records / job.total_records) * 100)}%)`
                : ''}
            </div>
            <div className="job-detail-item">
              <strong>Success Records:</strong> {job.success_records}
            </div>
            <div className="job-detail-item">
              <strong>Failed Records:</strong> {job.failed_records}
            </div>
            <div className="job-detail-item">
              <strong>Success Rate:</strong>{' '}
              {job.total_records > 0
                ? Math.round((job.success_records / job.total_records) * 100)
                : 0}
              %
            </div>
            <div className="job-detail-item">
              <strong>Duration:</strong>{' '}
              {formatDuration(job.job_start_time, job.job_end_time)}
            </div>
          </div>
        </div>
      </div>

      {hasDetails && (
        <div className="rt-section">
          <h5 className="mb-3" style={{ color: '#153C7F' }}>
            Detailed Results
          </h5>

          {Object.entries(parsedDetails)
            .filter(
              ([key, value]) =>
                !['summary', 'results', 'error'].includes(key) &&
                value !== null &&
                value !== undefined &&
                !(Array.isArray(value) && value.length === 0),
            )
            .map(([key, value]) => (
              <div key={key} className="job-detail-item">
                <strong>{humanizeKey(key)}:</strong>{' '}
                {Array.isArray(value)
                  ? value.length <= 20
                    ? value.map(String).join(', ')
                    : `${value.length} items`
                  : isPlainObject(value)
                    ? JSON.stringify(value)
                    : formatScalar(value)}
              </div>
            ))}

          {summaryEntries.length > 0 && (
            <div className="job-summary-box mb-3">
              <h6>Summary</h6>
              <div className="row">
                {summaryEntries.map(([key, value]) => (
                  <div key={key} className="col-md-4 mb-2">
                    <strong>{humanizeKey(key)}:</strong> {formatScalar(value)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {resultCategories.map((key) => (
            <div key={key} className="job-result-section mb-3">
              <h6 className={resultSectionTone(key)}>
                {humanizeKey(key)} ({parsedDetails.results[key].length})
              </h6>
              {renderResultList(parsedDetails.results[key])}
            </div>
          ))}

          {parsedDetails.error && (
            <div className="alert alert-danger">
              <strong>Error:</strong> {String(parsedDetails.error)}
            </div>
          )}
        </div>
      )}
    </>
  );

  if (!showMenu) {
    return content;
  }

  return (
    <RTPageLayout
      title={`Job Details: ${job.job_name}`}
      actions={
        <>
          <RTButton variant="outline-primary" size="sm" onClick={handleBack}>
            &#8592; Back
          </RTButton>
          <RTButton
            variant="primary"
            size="sm"
            onClick={handleExportCsv}
            disabled={exporting || !hasDetails || resultCategories.length === 0}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </RTButton>
          <RTButton
            variant="primary"
            size="sm"
            onClick={() => fetchJobDetails(jobId)}
            disabled={loading}
          >
            Refresh
          </RTButton>
        </>
      }
      mainSection={true}
      activePath={location.pathname}
    >
      {content}
    </RTPageLayout>
  );
};

export default ViewJobDetails;
